import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import styled from 'styled-components';

import { GetMicrostoreSitesQuery, GetMicrostoreSitesWithLocationsInfoQuery } from 'data/__generated__';

import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { DeleteMicrostoreColumnPopup } from '../Popups/DeleteMicrostoreColumnPopup';
import { CreateMicrostoreColumnPopup } from '../Popups/CreateMicrostoreColumnPopup';
import { CreateMicrostoreColumnGroupPopup } from 'pages/MicrostoreColumnGroups/Popups/CreateMicrostoreColumnGroupPopup';
import { DeleteMicrostoreColumnGroupPopup } from 'pages/MicrostoreColumnGroups/Popups/DeleteMicrostoreColumnGroupPopup';
import { MicrostoreColumnGroupEditor } from 'pages/MicrostoreColumnGroups/MicrostoreColumnGroupDetails/MicrostoreColumnGroupEditor';
import { EditColumnPopup } from 'pages/MicrostoreColumnGroups/Popups/EditColumnPopup';
import { ReplaceMicrostoreColumnPopup } from '../Popups/ReplaceMicrostoreColumnPopup';
import { ReplaceMicrostoreColumnTemplatePopup } from '../Popups/ReplaceMicrostoreColumnTemplatePopup';
import { MoveColumnPopup } from '../Popups/MoveColumnPopup';
import { ImShrink } from 'react-icons/im';

export const MicrostoreEditor = ({
    changeSelectedColumnId,
    currentSite,
    isOpen,
    setIsOpen,
}: {
    changeSelectedColumnId: (value: string | null) => void;
    currentSite: GetMicrostoreSitesWithLocationsInfoQuery['sites'][number];
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
}) => {
    const [isCreateMicrostoreColumnPopupOpen, setIsCreateMicrostoreColumnPopupOpen] = useState<boolean>(false);
    const [isDeleteMicrostoreColumnPopupOpen, setIsDeleteMicrostoreColumnPopupOpen] = useState<boolean>(false);

    const [isCreateMicrostoreColumnGroupPopupOpen, setIsCreateMicrostoreColumnGroupPopupOpen] =
        useState<boolean>(false);
    const [isDeleteMicrostoreColumnGroupPopupOpen, setIsDeleteMicrostoreColumnGroupPopupOpen] =
        useState<boolean>(false);

    const [microstoreColumnGroupToAddColumn, setMicrostoreColumnGroupToAddColumn] = useState<
        GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number] | null
    >(null);

    const [microstoreColumnToEdit, setMicrostoreColumnToEdit] = useState<
        | GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number]
        | null
    >(null);

    const [microstoreColumnToMove, setMicrostoreColumnToMove] = useState<
        | GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number]
        | null
    >(null);

    const [microstoreColumnToReplace, setMicrostoreColumnToReplace] = useState<
        | GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number]
        | null
    >(null);

    const [microstoreColumnToReplaceTemplate, setMicrostoreColumnToReplaceTemplate] = useState<
        | GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number]
        | null
    >(null);

    const [microstoreColumnToDelete, setMicrostoreColumnToDelete] = useState<
        | GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number]
        | null
    >(null);

    const [microstoreColumnGroupToDelete, setMicrostoreColumnGroupToDelete] = useState<
        GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number] | null
    >(null);

    return (
        <Container $isOpen={isOpen}>
            <Content>
                <HeaderContainer>
                    <SiteInfoContainer>
                        <Link to={`/microstore-snapshot/${currentSite._id}`}>
                            <TotemPrimaryButton type="button">Historique</TotemPrimaryButton>
                        </Link>
                        <SiteName>{currentSite.name}</SiteName>
                    </SiteInfoContainer>
                    <CloseButton size={20} onClick={() => setIsOpen(false)} />
                </HeaderContainer>
                <ColumnsGroupContainer>
                    {currentSite.microstoreColumnGroups.map((microstoreColumnGroup) => {
                        return (
                            <MicrostoreColumnGroupEditor
                                key={microstoreColumnGroup._id}
                                microstoreColumnGroup={microstoreColumnGroup}
                                addColumn={() => {
                                    changeSelectedColumnId(null);
                                    setMicrostoreColumnGroupToAddColumn(microstoreColumnGroup);
                                    setIsCreateMicrostoreColumnPopupOpen(true);
                                }}
                                editColumn={(
                                    microstoreColumn: GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number],
                                ) => {
                                    changeSelectedColumnId(null);
                                    setMicrostoreColumnToEdit(microstoreColumn);
                                }}
                                replaceColumn={(
                                    microstoreColumn: GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number],
                                ) => {
                                    changeSelectedColumnId(null);
                                    setMicrostoreColumnToReplace(microstoreColumn);
                                }}
                                replaceColumnTemplate={(
                                    microstoreColumn: GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number],
                                ) => {
                                    changeSelectedColumnId(null);
                                    setMicrostoreColumnToReplaceTemplate(microstoreColumn);
                                }}
                                deleteColumnGroup={() => {
                                    changeSelectedColumnId(null);
                                    setMicrostoreColumnGroupToDelete(microstoreColumnGroup);
                                    setIsDeleteMicrostoreColumnGroupPopupOpen(true);
                                }}
                                deleteColumn={(
                                    microstoreColumn: GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number],
                                ) => {
                                    changeSelectedColumnId(null);
                                    setMicrostoreColumnToDelete(microstoreColumn);
                                    setIsDeleteMicrostoreColumnPopupOpen(true);
                                }}
                                moveColumn={(
                                    microstoreColumn: GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number],
                                ) => {
                                    changeSelectedColumnId(null);
                                    setMicrostoreColumnToMove(microstoreColumn);
                                }}
                            />
                        );
                    })}

                    <AddColumnGroupButtonContainer>
                        <AddButton
                            type="button"
                            onClick={() => {
                                changeSelectedColumnId(null);
                                setIsCreateMicrostoreColumnGroupPopupOpen(true);
                            }}
                        >
                            <FaPlus size={20} />
                        </AddButton>
                    </AddColumnGroupButtonContainer>
                </ColumnsGroupContainer>
            </Content>

            {microstoreColumnGroupToAddColumn ? (
                <CreateMicrostoreColumnPopup
                    isOpen={isCreateMicrostoreColumnPopupOpen}
                    setIsOpen={setIsCreateMicrostoreColumnPopupOpen}
                    columnGroup={microstoreColumnGroupToAddColumn}
                />
            ) : null}
            {microstoreColumnToReplace ? (
                <ReplaceMicrostoreColumnPopup
                    isOpen={!!microstoreColumnToReplace}
                    setMicrostoreColumnToReplace={setMicrostoreColumnToReplace}
                    column={microstoreColumnToReplace}
                />
            ) : null}
            {microstoreColumnToReplaceTemplate ? (
                <ReplaceMicrostoreColumnTemplatePopup
                    isOpen={!!microstoreColumnToReplaceTemplate}
                    setMicrostoreColumnToReplaceTemplate={setMicrostoreColumnToReplaceTemplate}
                    column={microstoreColumnToReplaceTemplate}
                />
            ) : null}
            {microstoreColumnToEdit ? (
                <EditColumnPopup
                    isOpen={!!microstoreColumnToEdit}
                    setMicrostoreColumnToEdit={setMicrostoreColumnToEdit}
                    column={microstoreColumnToEdit}
                />
            ) : null}

            {microstoreColumnToMove ? (
                <MoveColumnPopup
                    isOpen={!!microstoreColumnToMove}
                    setMicrostoreColumnToMove={setMicrostoreColumnToMove}
                    column={microstoreColumnToMove}
                />
            ) : null}

            <CreateMicrostoreColumnGroupPopup
                isOpen={isCreateMicrostoreColumnGroupPopupOpen}
                setIsOpen={setIsCreateMicrostoreColumnGroupPopupOpen}
                site={currentSite}
            />
            <DeleteMicrostoreColumnPopup
                isOpen={isDeleteMicrostoreColumnPopupOpen}
                microstoreColumnToDelete={microstoreColumnToDelete}
                setIsOpen={setIsDeleteMicrostoreColumnPopupOpen}
            />

            {microstoreColumnGroupToDelete ? (
                <DeleteMicrostoreColumnGroupPopup
                    isOpen={isDeleteMicrostoreColumnGroupPopupOpen}
                    microstoreColumnGroupToDelete={microstoreColumnGroupToDelete}
                    setIsOpen={setIsDeleteMicrostoreColumnGroupPopupOpen}
                />
            ) : null}
        </Container>
    );
};

const Container = styled.div<{ $isOpen: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ $isOpen }) => ($isOpen ? '100%' : '0')};
    transition: width 0.6s ease-in-out;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    overflow: hidden;
    z-index: 2;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 25px;
    height: 100%;
    width: 100%;
`;

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const SiteInfoContainer = styled.div`
    display: flex;
    gap: 20px;
    justify-content: center;
    width: 100%;
`;

const ColumnsGroupContainer = styled.div`
    overflow: auto;
    padding: 0 60px;
    margin-top: 40px;

    // css tricks to have inside shadow when "in scroll"
    background:
        linear-gradient(white 30%, rgba(255, 255, 255, 0)) center top,
        linear-gradient(rgba(255, 255, 255, 0), white 70%) center bottom,
        radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)) center top,
        radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)) center bottom;

    background-repeat: no-repeat;
    background-size:
        100% 40px,
        100% 40px,
        100% 14px,
        100% 14px;
    background-attachment: local, local, scroll, scroll;

    // always display scrollbar so user know that it's scrollable
    ::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 7px;
    }

    ::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background-color: rgba(0, 0, 0, 0.5);
        box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
    }

    & > :not(:first-child) {
        margin-top: 30px;
    }
`;

const AddColumnGroupButtonContainer = styled.div`
    display: flex;
    justify-content: center;
`;

const NUMBER_SIZE = 70;

const AddButton = styled.button`
    cursor: pointer;
    height: ${NUMBER_SIZE}px;
    width: ${NUMBER_SIZE}px;
    border-radius: 100%;
    border: 2px solid ${({ theme }) => theme.darkBorderColor};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-shrink: 0;
`;

const SiteName = styled.div`
    text-align: center;
    font-size: 50px;
    font-weight: 800;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const CloseButton = styled(ImShrink)`
    cursor: pointer;
    margin-left: 25px;
`;
